/* eslint-disable @typescript-eslint/camelcase */
import cn from "classnames"
import React, { ChangeEvent, useRef, useState } from "react"
import DayPicker, { CaptionElementProps, DateUtils } from "react-day-picker"
import { useTranslation } from "react-i18next"

import { useSetState } from "~/helpers/useSetState"

import { Button } from "../Button/Button"
import styles from "./index.module.css"

type State = {
  date_start?: Date
  date_end?: Date
}

type Props = {
  className?: string
  onChange: (state: State) => void
  selectedDays: State
}

const currentYear = new Date().getFullYear()

// взята дата первой новости, но неплохо было бы брать нижнюю границу из апи. Можно запросить список без уточнения даты, тогда выдаст все возможные новости. Если еще добавить сортировку на бэке (?sort=desc), можно было бы сделать запрос на самую первую новость и вытащить дату из нее - /api/press/list?sort=desc&items_per_page=1&code=pressreleases
const fromMonth = new Date("2002-01-04")
// взят последний месяц текущего года, чтобы не заморачиваться с логикой типа если сейчас июль, то и в слекте за 2020 должен быть максимально июль
const toMonth = new Date(currentYear, 11)

const Select = ({ onChange, value, options, name }: any) => {
  const selectedValue = options.find((option: any) => option.value === value).text

  return (
    <div className={styles.Select}>
      <div style={{ pointerEvents: "none" }}>{selectedValue}</div>
      <select name={name} value={value} onChange={onChange}>
        {options!.map(({ value, text }: any) => (
          <option key={value} value={value}>
            {text}
          </option>
        ))}
      </select>
    </div>
  )
}

const YearMonthForm = ({
  date,
  onChange,
  months,
}: CaptionElementProps & { onChange: (e: any) => void }): JSX.Element => {
  const years: any[] = []
  for (let i = fromMonth.getFullYear(); i <= toMonth.getFullYear(); i += 1) {
    years.push(i)
  }

  const handleChange = (e: any) => {
    const { year, month } = e.target.form
    onChange(new Date(year.value, month.value))
  }

  return (
    <form className="DayPicker-Caption">
      <Select
        name="month"
        options={months!.map((month, i) => ({ value: i, text: month }))}
        value={date.getMonth()}
        onChange={handleChange}
      />
      <Select
        name="year"
        options={years.map((year) => ({ value: year, text: year }))}
        value={date.getFullYear()}
        onChange={handleChange}
      />
    </form>
  )
}

export const DatePicker: React.FC<Props> = ({ onChange, selectedDays, className }) => {
  const { t } = useTranslation()
  const [state, setState] = useSetState({
    start: selectedDays.date_start && new Date(selectedDays.date_start),
    end: selectedDays.date_end && new Date(selectedDays.date_end),
    enteredTo: selectedDays.date_end && new Date(selectedDays.date_end),
  })

  const [month, setMonth] = useState(new Date())

  const i18n = {
    locale: "ru",
    first_day_of_week: 1,
    months: [
      t("date.month.January"),
      t("date.month.February"),
      t("date.month.March"),
      t("date.month.April"),
      t("date.month.May"),
      t("date.month.June"),
      t("date.month.July"),
      t("date.month.August"),
      t("date.month.September"),
      t("date.month.October"),
      t("date.month.November"),
      t("date.month.December"),
    ],
    weekdays_short: [
      t("date.shortday.Sunday"),
      t("date.shortday.Monday"),
      t("date.shortday.Tuesday"),
      t("date.shortday.Wednesday"),
      t("date.shortday.Thursday"),
      t("date.shortday.Friday"),
      t("date.shortday.Saturday"),
    ],
    weekdays_long: [
      t("date.day.Sunday"),
      t("date.day.Monday"),
      t("date.day.Tuesday"),
      t("date.day.Wednesday"),
      t("date.day.Thursday"),
      t("date.day.Friday"),
      t("date.day.Saturday"),
    ],
    labels: { nextMonth: t("date.labels.next"), previousMonth: t("date.labels.prev") },
  }

  const handleResetClick = (): void => {
    onChange({ date_start: undefined, date_end: undefined })
    setState({ enteredTo: undefined })
  }

  const isSelectingFirstDay = (
    day: Date,
    date_start?: Date,
    date_end?: Date | undefined,
  ): boolean => {
    const isBeforeFirstDay = date_start && DateUtils.isDayBefore(day, date_start)
    const isRangeSelected = date_start && date_end

    return !date_start || !!isBeforeFirstDay || !!isRangeSelected
  }

  const handleDayClick = (day: Date): void => {
    const { start, end } = state

    if (start && end && day >= start && day <= end) {
      handleResetClick()
    }

    if (isSelectingFirstDay(day, start, end)) {
      setState({ start: day, end: undefined, enteredTo: undefined })
      onChange({ date_start: day, date_end: undefined })
    } else {
      setState({ start: start, end: day, enteredTo: day })
      onChange({ date_start: start, date_end: day })
    }
  }

  const handleDayMouseEnter = (day: Date): void => {
    const { start, end } = state

    if (!isSelectingFirstDay(day, start, end)) {
      setState({ enteredTo: day })
    }
  }

  const { start, end, enteredTo } = state

  const handleYearMonthChange = (month: any) => {
    setMonth(month)
  }

  return (
    <>
      <DayPicker
        captionElement={(props) => {
          return <YearMonthForm {...props} onChange={handleYearMonthChange} />
        }}
        className={cn(className, styles.Wrapper)}
        firstDayOfWeek={i18n.first_day_of_week}
        fromMonth={fromMonth}
        labels={i18n.labels}
        locale={i18n.locale}
        modifiers={{ start, end }}
        month={month}
        months={i18n.months}
        selectedDays={start && enteredTo && [start, { from: start, to: enteredTo }]}
        toMonth={toMonth}
        weekdaysLong={i18n.weekdays_long}
        weekdaysShort={i18n.weekdays_short}
        onDayClick={handleDayClick}
        onDayMouseEnter={handleDayMouseEnter}
      />
      <Button
        className={styles.ClearButton}
        // data-state={cn({ clear: state. && date_end })}
        view="rounded"
        onClick={() => {
          const firstDayofTheYear = new Date(new Date(month).getFullYear(), 0, 1)
          const lastDayofTheYear = new Date(new Date(month).getFullYear(), 11, 31)

          setState({ start: firstDayofTheYear, end: lastDayofTheYear, enteredTo: lastDayofTheYear })
          onChange({ date_start: firstDayofTheYear, date_end: lastDayofTheYear })
        }}
      >
        {t("date.picker.getFullYear")}
      </Button>
    </>
  )
}
